import { COMMON_API_URL, IMAGE_URL } from '../api/client';

export interface ImageUploadResponse {
  url: string; // The full public URL for immediate display
  fileName: string; // The S3 key/path (e.g., 'temp/dev-3U-...') for the API
}

/**
 * Upload image using S3 pre-signed PUT URL
 */
export const uploadImage = async (file: File): Promise<ImageUploadResponse> => {
  try {
    const epochNow = new Date().getTime();
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
    const randomFileName = 'dev-3U-' + epochNow + '.' + fileExtension;
    const s3Key = `temp/${randomFileName}`;
    console.log('Generated S3 key for upload:', COMMON_API_URL);

    // 1️⃣ Get pre-signed PUT URL from API
    const response = await fetch(`${COMMON_API_URL}pre-signed`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fileName: s3Key,
        fileType: file.type,
      }),
    });

    const data = await response.json();
    if (!data?.url) {
      throw new Error('Invalid pre-signed URL response from server');
    }

    const uploadUrl = data.url;

    // 2️⃣ Upload file to S3 using PUT
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      // FIX: body must be the 'File' object
      body: file,
    });

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      console.error('Upload to S3 failed (HTTP status:', uploadResponse.status, '):', errorText);
      throw new Error(`Failed to upload file to S3 (Status: ${uploadResponse.status})`);
    }

    // 3️⃣ Final S3 URL without query params
    const finalUrl = uploadUrl.split('?')[0];

    return {
      url: finalUrl,
      // Returning the S3 key which is what the API expects to save in the database
      fileName: randomFileName,
    };
  } catch (error: any) {
    console.error('Image upload failed:', error.message);
    throw error;
  }
};

/**
 * Validate Image File (omitted for brevity, assume it is unchanged and correct)
 */
export const validateImageFile = (file: File): string | null => {
  if (!file.type.startsWith('image/')) return 'Please select an image file';

  const maxSize = 5 * 1024 * 1024;
  if (file.size > maxSize) return 'Image size should be less than 5MB';

  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

  if (!allowedTypes.includes(file.type)) {
    return 'Only JPEG, PNG, GIF, and WebP images are allowed';
  }

  return null;
};
